<template>
    <transition name="quick-shelf-picker">
        <div v-if="show" v-quick-shelf-portal class="quick-shelf-picker__portal" @keydown="handleKeydown">
            <button
                type="button"
                class="quick-shelf-picker__backdrop"
                aria-label="Закрити вибір полиці"
                @click="$emit('close')"
            ></button>
            <section
                ref="panel"
                class="quick-shelf-picker__panel"
                :style="panelStyle"
                role="dialog"
                aria-modal="true"
                aria-labelledby="quick-shelf-picker-title"
            >
                <header class="flex items-center justify-between gap-3 border-b border-foxy-border/70 px-4 py-3">
                    <div>
                        <h3 id="quick-shelf-picker-title" class="text-sm font-extrabold text-foxy-text">Оберіть полицю</h3>
                        <p class="mt-0.5 text-xs text-foxy-text-secondary">Статус можна змінити будь-коли</p>
                    </div>
                    <button
                        type="button"
                        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-foxy-text-secondary hover:bg-foxy-elevated hover:text-foxy-text"
                        aria-label="Закрити"
                        @click="$emit('close')"
                    >
                        <i class="fas fa-times" aria-hidden="true"></i>
                    </button>
                </header>

                <div class="grid gap-2 p-3" role="listbox" aria-label="Полиці бібліотеки">
                    <button
                        v-for="option in options"
                        :key="option.status"
                        ref="options"
                        type="button"
                        class="quick-shelf-picker__option"
                        :class="`quick-shelf-picker__option--${option.token}`"
                        :aria-selected="currentStatus === option.status ? 'true' : 'false'"
                        :disabled="busy"
                        role="option"
                        @click="$emit('select', option.status)"
                    >
                        <span class="quick-shelf-picker__icon" aria-hidden="true">
                            <i v-if="option.icon" class="fas" :class="option.icon"></i>
                        </span>
                        <span class="min-w-0 flex-1 text-left font-bold">{{ option.label }}</span>
                        <i v-if="currentStatus === option.status" class="fas fa-check text-xs" aria-hidden="true"></i>
                    </button>
                </div>
            </section>
        </div>
    </transition>
</template>

<script>
const VIEWPORT_PADDING = 12;
const POPOVER_GAP = 8;

export default {
    name: 'QuickShelfPicker',
    directives: {
        quickShelfPortal: {
            inserted(element) {
                if (element.parentNode !== document.body) document.body.appendChild(element);
            },
        },
    },
    props: {
        show: { type: Boolean, default: false },
        currentStatus: { type: String, default: null },
        anchorElement: { type: null, default: null },
        busy: { type: Boolean, default: false },
    },
    data() {
        return {
            panelStyle: { visibility: 'hidden' },
            options: [
                { status: 'want_to_read', label: 'Хочу прочитати', icon: '', token: 'want' },
                { status: 'reading', label: 'Читаю', icon: 'fa-book-open', token: 'reading' },
                { status: 'read', label: 'Прочитано', icon: 'fa-check', token: 'read' },
                { status: 'abandoned', label: 'Не дочитано', icon: 'fa-minus', token: 'abandoned' },
            ],
        };
    },
    watch: {
        show: {
            immediate: true,
            handler(isOpen) {
                if (isOpen) this.open();
                else this.teardown();
            },
        },
    },
    methods: {
        open() {
            window.addEventListener('resize', this.position);
            window.addEventListener('scroll', this.position, true);
            document.addEventListener('mousedown', this.handleOutsidePointer);
            this.$nextTick(() => {
                this.position();
                const options = Array.isArray(this.$refs.options) ? this.$refs.options : [];
                const selectedIndex = Math.max(0, this.options.findIndex((option) => option.status === this.currentStatus));
                options[selectedIndex]?.focus();
            });
        },
        teardown() {
            window.removeEventListener('resize', this.position);
            window.removeEventListener('scroll', this.position, true);
            document.removeEventListener('mousedown', this.handleOutsidePointer);
            this.panelStyle = { visibility: 'hidden' };
        },
        position() {
            const panel = this.$refs.panel;
            if (!panel) return;
            if (window.innerWidth < 640) {
                this.panelStyle = { visibility: 'visible' };
                return;
            }
            const anchor = this.anchorElement;
            if (!anchor?.getBoundingClientRect) return;
            const rect = anchor.getBoundingClientRect();
            const width = panel.offsetWidth || 320;
            const height = panel.offsetHeight || 278;
            let left = rect.left;
            left = Math.max(VIEWPORT_PADDING, Math.min(left, window.innerWidth - width - VIEWPORT_PADDING));
            let top = rect.bottom + POPOVER_GAP;
            if (top + height > window.innerHeight - VIEWPORT_PADDING) top = rect.top - height - POPOVER_GAP;
            top = Math.max(VIEWPORT_PADDING, Math.min(top, window.innerHeight - height - VIEWPORT_PADDING));
            this.panelStyle = {
                left: `${Math.round(left)}px`,
                top: `${Math.round(top)}px`,
                visibility: 'visible',
            };
        },
        handleOutsidePointer(event) {
            if (window.innerWidth < 640) return;
            if (this.$refs.panel?.contains(event.target) || this.anchorElement?.contains(event.target)) return;
            this.$emit('close');
        },
        handleKeydown(event) {
            if (event.key === 'Escape') {
                event.preventDefault();
                this.$emit('close', { restoreFocus: true });
                return;
            }
            if (event.key !== 'Tab') return;
            const focusable = Array.from(this.$refs.panel?.querySelectorAll('button:not([disabled])') || []);
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        },
    },
    beforeDestroy() {
        this.teardown();
    },
};
</script>

<style scoped>
.quick-shelf-picker__portal {
    position: fixed;
    inset: 0;
    z-index: 115;
    pointer-events: none;
}
.quick-shelf-picker__backdrop {
    display: none;
}
.quick-shelf-picker__panel {
    position: fixed;
    width: min(20rem, calc(100vw - 1.5rem));
    overflow: hidden;
    border: 1px solid rgb(var(--foxy-border));
    border-radius: 1rem;
    background: rgb(var(--foxy-surface));
    box-shadow: 0 18px 50px rgb(var(--foxy-cocoa) / 0.25);
    color: rgb(var(--foxy-text));
    pointer-events: auto;
}
.quick-shelf-picker__option {
    display: flex;
    min-height: 2.85rem;
    align-items: center;
    gap: 0.75rem;
    border: 1px solid rgb(var(--foxy-border));
    border-radius: 0.75rem;
    padding: 0.55rem 0.75rem;
    color: rgb(var(--foxy-text));
    transition: background-color 150ms ease, border-color 150ms ease;
}
.quick-shelf-picker__option:hover,
.quick-shelf-picker__option:focus-visible {
    background: rgb(var(--foxy-elevated));
    outline: none;
}
.quick-shelf-picker__option[aria-selected='true'] {
    border-color: var(--shelf-color);
    background: color-mix(in srgb, var(--shelf-color) 14%, rgb(var(--foxy-surface)));
}
.quick-shelf-picker__icon {
    display: inline-flex;
    width: 1.75rem;
    height: 1.75rem;
    align-items: center;
    justify-content: center;
    flex: 0 0 1.75rem;
    border-radius: 9999px;
    background: var(--shelf-color);
    color: #fffaf7;
    font-size: 0.72rem;
}
.quick-shelf-picker__option--want { --shelf-color: #5b458c; }
.quick-shelf-picker__option--want .quick-shelf-picker__icon { border: 2px solid #5b458c; background: transparent; }
.quick-shelf-picker__option--reading { --shelf-color: #2e6095; }
.quick-shelf-picker__option--read { --shelf-color: #356b4a; }
.quick-shelf-picker__option--abandoned { --shelf-color: #9a4b3d; }

@media (max-width: 639px) {
    .quick-shelf-picker__portal {
        pointer-events: auto;
    }
    .quick-shelf-picker__backdrop {
        position: absolute;
        inset: 0;
        display: block;
        width: 100%;
        border: 0;
        background: rgb(var(--foxy-cocoa) / 0.5);
        backdrop-filter: blur(2px);
    }
    .quick-shelf-picker__panel {
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        max-height: min(80dvh, 32rem);
        border-right: 0;
        border-bottom: 0;
        border-left: 0;
        border-radius: 1.25rem 1.25rem 0 0;
        padding-bottom: max(0.5rem, env(safe-area-inset-bottom));
    }
}
.quick-shelf-picker-enter-active,
.quick-shelf-picker-leave-active {
    transition: opacity 160ms ease;
}
.quick-shelf-picker-enter,
.quick-shelf-picker-leave-to {
    opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
    .quick-shelf-picker-enter-active,
    .quick-shelf-picker-leave-active {
        transition: none;
    }
}
</style>
